import React from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import RemoveIcon from '@material-ui/icons/Remove';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { useFontSize } from '../../contexts/FontSizeContext';
import { updateProductQty, parseProductData } from '../../actions/dineIn';

const useStyles = makeStyles(() => ({
    mealItemContainer: {
        display: 'flex',
        flexDirection: 'column',
        padding: '12px 0',
        backgroundColor: 'white',
        borderRadius: '8px',
        gap: '12px',
    },
    mealInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    controlsRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
    },
    mealTitle: {
        fontWeight: 'bold',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: '1.3',
    },
    specsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
    },
    specLine: {
        color: '#666666',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: '1.2',
    },
    simpleOptionsContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
    },
    simpleOptionLine: {
        color: '#666666',
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        lineHeight: '1.2',
    },
    price: {
        fontWeight: 'bold',
        color: '#2e7d32',
        minWidth: 'fit-content',
    },
    quantityControls: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        backgroundColor: '#f5f5f5',
        borderRadius: '20px',
        padding: '4px',
    },
    qtyButton: {
        width: '32px',
        height: '32px',
        padding: '4px',
        backgroundColor: 'white',
        border: '1px solid #e0e0e0',
        borderRadius: '50%',
        '&:hover': {
            backgroundColor: '#f0f0f0',
        },
        '& .MuiSvgIcon-root': {
            fontSize: '18px',
            color: '#666666',
        },
        '&:disabled .MuiSvgIcon-root': {
            color: '#c0c0c0',
        },
        '& .MuiSvgIcon-root[data-testid="DeleteIcon"]': {
            color: '#d32f2f',
        },
    },
    quantity: {
        minWidth: '24px',
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: '16px',
    },
}));

const MealItem = ({
    itemName,
    specs = [],
    simpleOptions = [],
    quantity,
    unitPrice,
    productIdx,
    specIdx = null,
    minQuantity = 1,
    onQuantityChange,
    showSeparator = true,
}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { getFontSize } = useFontSize();

    // Dynamic font sizes
    const mealTitleFontSize = getFontSize('mealTitle');
    const subtitleFontSize = getFontSize('subtitle');
    const priceFontSize = getFontSize('priceMain');

    const handleIncreaseQty = (e) => {
        e.stopPropagation();
        const newQty = quantity + 1;
        dispatch(updateProductQty(newQty, productIdx, specIdx || 0));
        // Clean up zero-quantity items like the original EditDialog did
        dispatch(parseProductData());
        
        if (onQuantityChange) {
            onQuantityChange(newQty);
        }
    };

    const handleDecreaseQty = (e) => {
        e.stopPropagation();
        if (quantity >= 1) {
            const newQty = quantity - 1;
            dispatch(updateProductQty(newQty, productIdx, specIdx || 0));
            // Clean up zero-quantity items like the original EditDialog did
            dispatch(parseProductData());
            
            if (onQuantityChange) {
                onQuantityChange(newQty);
            }
        }
    };

    const totalPrice = (quantity * unitPrice).toFixed(2);

    // Group specs by line with dash prefixes
    const renderSpecs = () => {
        if (!specs || specs.length === 0) return null;

        return (
            <div className={classes.specsContainer}>
                {specs.map((spec, index) => (
                    <Typography
                        key={index}
                        className={classes.specLine}
                        variant="inherit"
                        style={{ fontSize: subtitleFontSize }}
                    >
                        - {spec}
                    </Typography>
                ))}
            </div>
        );
    };

    // Group simple options by line with dash prefixes
    const renderSimpleOptions = () => {
        if (!simpleOptions || simpleOptions.length === 0) return null;

        return (
            <div className={classes.simpleOptionsContainer}>
                {simpleOptions.map((option, index) => (
                    <Typography
                        key={index}
                        className={classes.simpleOptionLine}
                        variant="inherit"
                        style={{ fontSize: subtitleFontSize }}
                    >
                        - {option}
                    </Typography>
                ))}
            </div>
        );
    };

    return (
        <div className={classes.mealItemContainer}>
            {/* Meal Information Section */}
            <div className={classes.mealInfo}>
                <Typography
                    className={classes.mealTitle}
                    variant="inherit"
                    style={{ fontSize: mealTitleFontSize }}
                >
                    {itemName}
                </Typography>

                {renderSpecs()}
                {renderSimpleOptions()}
            </div>

            {/* Controls Row - Quantity Controls and Price */}
            <div className={classes.controlsRow}>
                <div className={classes.quantityControls}>
                    <IconButton
                        className={classes.qtyButton}
                        onClick={handleDecreaseQty}
                        size="small"
                        aria-label={quantity === 1 ? "delete item" : "decrease quantity"}
                    >
                        {quantity === 1 ? <DeleteIcon /> : <RemoveIcon />}
                    </IconButton>

                    <Typography
                        className={classes.quantity}
                        variant="inherit"
                    >
                        {quantity}
                    </Typography>

                    <IconButton
                        className={classes.qtyButton}
                        onClick={handleIncreaseQty}
                        size="small"
                        aria-label="increase quantity"
                    >
                        <AddIcon />
                    </IconButton>
                </div>

                <Typography
                    className={classes.price}
                    variant="inherit"
                    style={{ fontSize: priceFontSize }}
                >
                    ${totalPrice}
                </Typography>
            </div>
        </div>
    );
};

export default MealItem;
